import { StatusCodes } from '@e3dc-react/shell/Libs'
import { AuthFetchWithResponse } from '@e3dc-react/shell/Types/api'
import { ApiResult } from 'src/Libs/api'
import fromDtoToModelTransformer from './transformer/admin-customer-list-from-dto-to-model'
import { AdminCustomerListDto, AdminCustomerListPayload } from './dto/admin-customer-list.dto'
import { AdminCustomerListModel, AdminCustomerLoginResponse, AdminShopPermissionModel, PromotionCodeOverviewResponse } from '../models'
import { ShopAuthFetchWithResponse } from '../types'
import { handleFetchError } from 'src/Libs/api/utils'
import axios, { AxiosResponse } from 'axios'

export const fetchCustomerList = async (authFetch: AuthFetchWithResponse, filter?: string): Promise<ApiResult<AdminCustomerListModel[]>> => {
  try {
    const payload: AdminCustomerListPayload = {
      filter: 'permissions=useShop',
      connect: 'company',
      fields: 'company.Name,company.sapCustomerID,user.username,user.ID',
    }
    if (filter) {
      payload.filterOr = `user.username=*${filter}*,user.ID=*${filter}*,company.sapCustomerID=*${filter}*,company.Name=*${filter}*`
    }

    const response = await authFetch<AdminCustomerListDto>({
      path: 'users',
      method: 'GET',
      payload,
    })

    if (response.status === StatusCodes.OK) {
      const model = fromDtoToModelTransformer(response.data)
      return new ApiResult<AdminCustomerListModel[]>(model)
    }
    return new ApiResult<AdminCustomerListModel[]>(null, true)
  } catch (error) {
    return handleFetchError<AdminCustomerListModel[]>(error)
  }
}

export const fetchCustomerShopPermission = async (portalAdminToken: string): Promise<ApiResult<AdminShopPermissionModel>> => {
  try {
    const response: AxiosResponse = await axios.post(
      `${process.env.REACT_APP_SHOP_API_ENDPOINT}/e3dc/has-impersonate-login-permission`,
      { portalAdminToken },
      {
        headers: {
          'sw-access-key': process.env.REACT_APP_SHOP_API_ACCESS_KEY,
        },
      }
    )

    if (response.status === 200) {
      return new ApiResult<AdminShopPermissionModel>(response.data as AdminShopPermissionModel)
    }

    return new ApiResult<AdminShopPermissionModel>(null, true)
  } catch (error) {
    return handleFetchError<AdminShopPermissionModel>(error)
  }
}

export const fetchCustomerToken = async (
  portalAdminToken: string,
  portalUserId: number,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<AdminCustomerLoginResponse>> => {
  try {
    const response = await shopFetch({
      path: 'e3dc/impersonate-login',
      method: 'POST',
      payload: {
        portalAdminToken,
        portalUserId,
      },
      currentShopAccountToken: true,
      defaultShopAccessKey: true,
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<AdminCustomerLoginResponse>(response.data as AdminCustomerLoginResponse)
    }

    return new ApiResult<AdminCustomerLoginResponse>(null)
  } catch (error) {
    return handleFetchError<AdminCustomerLoginResponse>(error)
  }
}

export const fetchPromotionCodeOverview = async (shopFetch: ShopAuthFetchWithResponse): Promise<ApiResult<PromotionCodeOverviewResponse>> => {
  try {
    const response = await shopFetch({
      path: 'e3dc/staff/voucher-info',
      method: 'GET',
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<PromotionCodeOverviewResponse>(response.data as PromotionCodeOverviewResponse)
    }

    return new ApiResult<PromotionCodeOverviewResponse>(null)
  } catch (error) {
    return handleFetchError<PromotionCodeOverviewResponse>(error)
  }
}

export const addPromotionCode = async (
  amount: number,
  shopFetch: ShopAuthFetchWithResponse,
  note?: string
): Promise<ApiResult<PromotionCodeOverviewResponse>> => {
  try {
    const response = await shopFetch({
      path: 'e3dc/staff/voucher-create',
      method: 'POST',
      payload: { amount, note },
    })
    return new ApiResult<PromotionCodeOverviewResponse>(response.data as PromotionCodeOverviewResponse)
  } catch (error) {
    return handleFetchError<PromotionCodeOverviewResponse>(error)
  }
}

export const deletePromotionCode = async (
  promoCodeId: string,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<PromotionCodeOverviewResponse>> => {
  try {
    const response = await shopFetch({
      path: `e3dc/staff/voucher-delete/${promoCodeId}`,
      method: 'DELETE',
    })
    return new ApiResult<PromotionCodeOverviewResponse>(response.data as PromotionCodeOverviewResponse)
  } catch (error) {
    return handleFetchError<PromotionCodeOverviewResponse>(error)
  }
}

export const editPromotionCode = async (
  promoCodeId: string,
  amount: number,
  shopFetch: ShopAuthFetchWithResponse,
  note?: string | null
): Promise<ApiResult<PromotionCodeOverviewResponse>> => {
  try {
    const response = await shopFetch({
      path: `e3dc/staff/voucher-update/${promoCodeId}`,
      method: 'PUT',
      payload: { amount, note },
    })
    return new ApiResult<PromotionCodeOverviewResponse>(response.data as PromotionCodeOverviewResponse)
  } catch (error) {
    return handleFetchError<PromotionCodeOverviewResponse>(error)
  }
}
